import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { FaSubtask, FaTask, FaTaskPriority, FaTaskRecurrence, FaTaskStatus } from '../fa-tasks.model';
import { Opportunity } from '../client-actions.model';

export interface FaTaskDialogResult {
  patch: Partial<FaTask>;
  subtasks: FaSubtask[];
}

interface DialogData {
  task: FaTask;
  opportunities: Opportunity[];
}

const STATUS_OPTIONS: { label: string; value: FaTaskStatus }[] = [
  { label: 'To Do', value: 'To Do' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Done', value: 'Done' },
  { label: 'Blocked', value: 'Blocked' },
  { label: 'Withdrawn', value: 'Withdrawn' },
];

const PRIORITY_OPTIONS: { label: string; value: FaTaskPriority }[] = [
  { label: 'P1 — Critical', value: 'P1' },
  { label: 'P2 — Important', value: 'P2' },
  { label: 'P3 — Normal', value: 'P3' },
  { label: 'P4 — No Priority', value: 'P4' },
];

const RECURRENCE_OPTIONS: { label: string; value: FaTaskRecurrence }[] = [
  { label: 'None', value: 'none' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Custom', value: 'custom' },
];

@Component({
  selector: 'bai-fa-task-details-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, InputTextModule, SelectModule, TagModule, TextareaModule],
  template: `
    <div class="flex flex-col gap-4 p-2">

      <!-- Task name -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="ftd-name">
          Task Name <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="ftd-name"
          pInputText
          [ngModel]="form().taskName"
          (ngModelChange)="patch('taskName', $event)"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().taskName) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <!-- Description -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="ftd-desc">Description</label>
        <textarea
          id="ftd-desc"
          pTextarea
          [ngModel]="form().description"
          (ngModelChange)="patch('description', $event)"
          rows="2"
          class="w-full"
        ></textarea>
      </div>

      <!-- Assignee -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="ftd-assignee">
          Assignee <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="ftd-assignee"
          pInputText
          [ngModel]="form().assignee"
          (ngModelChange)="patch('assignee', $event)"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().assignee) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <!-- Linked Opportunity -->
      @if (opportunityOptions.length > 0) {
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ftd-opp">Linked Opportunity</label>
          <p-select
            inputId="ftd-opp"
            [options]="opportunityOptions"
            [ngModel]="form().opportunityId"
            (ngModelChange)="patch('opportunityId', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="None"
            styleClass="w-full"
            [showClear]="true"
          />
        </div>
      }

      <!-- Priority + Status -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ftd-priority">Priority</label>
          <p-select
            inputId="ftd-priority"
            [options]="priorityOptions"
            [ngModel]="form().priority"
            (ngModelChange)="patch('priority', $event)"
            optionLabel="label"
            optionValue="value"
            styleClass="w-full"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ftd-status">Status</label>
          <p-select
            inputId="ftd-status"
            [options]="statusOptions"
            [ngModel]="form().status"
            (ngModelChange)="patch('status', $event)"
            optionLabel="label"
            optionValue="value"
            styleClass="w-full"
          />
        </div>
      </div>

      <!-- Due Date + Planned Date -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ftd-due">
            Due Date <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="ftd-due"
            pInputText
            type="date"
            [ngModel]="form().dueDate"
            (ngModelChange)="patch('dueDate', $event)"
            class="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().dueDate) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ftd-planned">Planned Date</label>
          <input
            id="ftd-planned"
            pInputText
            type="date"
            [ngModel]="form().plannedDate"
            (ngModelChange)="patch('plannedDate', $event)"
            class="w-full"
          />
        </div>
      </div>

      <!-- Labels -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="ftd-labels">
          Labels <span class="text-xs text-gray-400">(comma-separated)</span>
        </label>
        <input
          id="ftd-labels"
          pInputText
          [ngModel]="labelsInput()"
          (ngModelChange)="labelsInput.set($event)"
          placeholder="e.g. IRA, Review"
          class="w-full"
          aria-label="Labels, comma-separated"
        />
      </div>

      <!-- Recurrence -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="ftd-recurrence">Recurrence</label>
        <p-select
          inputId="ftd-recurrence"
          [options]="recurrenceOptions"
          [ngModel]="form().recurrence"
          (ngModelChange)="patch('recurrence', $event)"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full"
        />
      </div>

      @if (form().recurrence === 'custom') {
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ftd-rec-text">Custom Recurrence</label>
          <input
            id="ftd-rec-text"
            pInputText
            [ngModel]="form().recurrenceCustomText"
            (ngModelChange)="patch('recurrenceCustomText', $event)"
            class="w-full"
          />
        </div>
      }

      <!-- Subtasks -->
      @if (subtasks().length > 0) {
        <div class="flex flex-col gap-2 pt-1 border-t border-gray-100">
          <div class="flex items-center justify-between">
            <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Subtasks</span>
            <span class="text-xs text-gray-400">{{ doneCount() }}/{{ subtasks().length }} done</span>
          </div>
          <div class="w-full bg-gray-100 rounded-full h-1.5">
            <div
              class="bg-blue-500 h-1.5 rounded-full transition-all"
              [style.width]="progressPercent() + '%'"
            ></div>
          </div>
          <ul class="flex flex-col gap-1" role="list" aria-label="Subtasks">
            @for (sub of subtasks(); track sub.id) {
              <li class="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  [id]="'sub-' + sub.id"
                  [checked]="sub.done"
                  (change)="toggleSubtask(sub)"
                  class="cursor-pointer accent-blue-500"
                  [attr.aria-label]="sub.title"
                />
                <label
                  [for]="'sub-' + sub.id"
                  class="cursor-pointer"
                  [class.line-through]="sub.done"
                  [class.text-gray-400]="sub.done"
                >{{ sub.title }}</label>
              </li>
            }
          </ul>
        </div>
      }

      <!-- Metadata -->
      <div class="text-xs text-gray-400 pt-1 border-t border-gray-100">
        Created {{ task.createdDate }} · Updated {{ task.updatedDate }}
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-2 pt-2">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="cancel()" />
        <p-button label="Save" (onClick)="save()" />
      </div>

    </div>
  `,
  styles: `
    .fat-label-chip {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      font-size: 0.7rem;
      font-weight: 500;
      border-radius: 9999px;
      background-color: #eff6ff;
      color: #1d4ed8;
      border: 1px solid #bfdbfe;
    }
  `,
})
export class FaTaskDetailsDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);

  readonly task: FaTask;
  readonly opportunityOptions: { label: string; value: string }[];
  readonly statusOptions = STATUS_OPTIONS;
  readonly priorityOptions = PRIORITY_OPTIONS;
  readonly recurrenceOptions = RECURRENCE_OPTIONS;
  readonly submitted = signal(false);

  readonly subtasks: ReturnType<typeof signal<FaSubtask[]>>;
  readonly labelsInput: ReturnType<typeof signal<string>>;

  readonly form: ReturnType<typeof signal<{
    taskName: string;
    description: string;
    assignee: string;
    opportunityId: string | null;
    priority: FaTaskPriority;
    status: FaTaskStatus;
    dueDate: string;
    plannedDate: string;
    recurrence: FaTaskRecurrence;
    recurrenceCustomText: string;
  }>>;

  constructor() {
    const data = this._config.data as DialogData;
    this.task = data.task;
    this.opportunityOptions = (data.opportunities ?? []).map((o) => ({
      label: o.opportunityDescription,
      value: o.id,
    }));
    this.subtasks = signal([...data.task.subtasks]);
    this.labelsInput = signal(data.task.labels.join(', '));
    this.form = signal({
      taskName: this.task.taskName,
      description: this.task.description,
      assignee: this.task.assignee,
      opportunityId: this.task.opportunityId,
      priority: this.task.priority,
      status: this.task.status,
      dueDate: this.task.dueDate,
      plannedDate: this.task.plannedDate,
      recurrence: this.task.recurrence,
      recurrenceCustomText: this.task.recurrenceCustomText ?? '',
    });
  }

  readonly doneCount = computed(() => this.subtasks().filter((s) => s.done).length);
  readonly progressPercent = computed(() => {
    const total = this.subtasks().length;
    return total === 0 ? 0 : Math.round((this.doneCount() / total) * 100);
  });

  patch<K extends keyof ReturnType<typeof this.form>>(key: K, value: ReturnType<typeof this.form>[K]): void {
    this.form.update((f) => ({ ...f, [key]: value }));
  }

  toggleSubtask(sub: FaSubtask): void {
    this.subtasks.update((list) =>
      list.map((s) => (s.id === sub.id ? { ...s, done: !s.done } : s))
    );
  }

  save(): void {
    this.submitted.set(true);
    const f = this.form();
    if (!f.taskName || !f.assignee || !f.dueDate) return;

    const labels = this.labelsInput()
      .split(',')
      .map((l) => l.trim())
      .filter(Boolean);

    const result: FaTaskDialogResult = {
      patch: { ...f, labels },
      subtasks: this.subtasks(),
    };
    this._ref.close(result);
  }

  cancel(): void {
    this._ref.close(null);
  }
}
